import { Directive, Input, ElementRef, OnDestroy } from '@angular/core';
import { Validator, AbstractControl, ValidationErrors, Validators, NG_VALIDATORS } from '@angular/forms';
import { DynamicFieldsManagerService } from 'src/app/services/dynamic-fields/dynamic-fields-manager.service';
import { RestProviderService } from 'src/app/services/rest-provider/rest-provider.service';
import { Subscription } from 'rxjs';

@Directive({
    selector: '[semrequired]',
    providers: [{ provide: NG_VALIDATORS, useExisting: SemRequiredDirective, multi: true }],
    standalone: false
})
export class SemRequiredDirective implements Validator, OnDestroy {

  @Input() semrequired: string;
  cntrl: AbstractControl;
  private mtoSub: Subscription;
  private connectedRecheck: any = null;

  constructor(
    public dfms: DynamicFieldsManagerService,
    public restProviderService: RestProviderService,
    private el: ElementRef
  ) {
    // DO NOT set data-semrequired here — it is set dynamically by setRequired()/setNotRequired()
    // based on actual required state (MTO required flag + MTO data presence).

    this.mtoSub = this.dfms._modelMTOValueChanged.subscribe(model => {
      if (!this.semrequired || !this.cntrl) return;

      const mto = this.findMatchingMTO();
      if (!mto) return; // No MTO match — handled by validate() fallthrough

      if (!mto.required) {
        // MTO is not required — field never required
        this.setNotRequired();
        return;
      }

      // MTO IS required — check if it has data selected
      if (model == null) {
        this.setNotRequired();
        return;
      }

      let res = this.dfms.getValue(model, mto.name);
      if (typeof res != "undefined" && res != null && Object.keys(res).length > 1) {
        this.setRequired();
      } else {
        this.setNotRequired();
      }
    });
  }

  validate(control: AbstractControl): ValidationErrors {
    this.cntrl = control;

    const mto = this.findMatchingMTO();
    if (mto) {
      if (mto.required) {
        this.setRequired();
      } else {
        this.setNotRequired();
      }
      return null;
    }

    // No MTO match — standalone required field, ALWAYS required
    this.setRequired();
    return null;
  }

  ngOnDestroy() {
    if (this.mtoSub) {
      this.mtoSub.unsubscribe();
    }
    if (this.connectedRecheck) {
      clearTimeout(this.connectedRecheck);
      this.connectedRecheck = null;
    }
  }

  /**
   * The backend renders one-to-many child controls as projected content of <sem-one-to-many-container>,
   * which has no <ng-content>. Angular still instantiates them once per container, never attached to the
   * document, bound to the root model (value undefined) and registered in mainForm. Such a "phantom"
   * control must never carry a required validator, otherwise the form is invalid with zero rows.
   */
  private isConnected(): boolean {
    const el = this.el.nativeElement;
    if (!el) return false;
    if (typeof el.isConnected === 'boolean') return el.isConnected;
    return document.contains(el);
  }

  private findMatchingMTO(): { name: string; required: boolean } | null {
    if (!this.semrequired) return null;
    for (let i = 0; i < this.dfms.manyToOne.length; i++) {
      if (this.semrequired.includes(this.dfms.manyToOne[i].name)) {
        return this.dfms.manyToOne[i];
      }
    }
    return null;
  }

  private isCheckbox(): boolean {
    const tag = this.el.nativeElement.tagName?.toLowerCase();
    if (tag === 'sem-check-box') return true;
    if (tag === 'input' && this.el.nativeElement.type === 'checkbox') return true;
    return false;
  }

  private setRequired() {
    if (!this.isConnected()) {
      // Phantom (see isConnected) - or a real control validated before it was attached. Keep it
      // non-required now and re-evaluate once; a phantom never attaches, a real control will have.
      this.setNotRequired();
      if (!this.connectedRecheck) {
        this.connectedRecheck = setTimeout(() => {
          this.connectedRecheck = null;
          if (this.cntrl && this.isConnected()) {
            this.validate(this.cntrl);
          }
        }, 0);
      }
      return;
    }

    this.el.nativeElement.setAttribute('data-semrequired', '');
    if (this.cntrl) {
      const validator = this.isCheckbox() ? Validators.requiredTrue : Validators.required;
      this.cntrl.setValidators([validator]);
      this.cntrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private setNotRequired() {
    this.el.nativeElement.removeAttribute('data-semrequired');
    if (this.cntrl) {
      this.cntrl.clearValidators();
      this.cntrl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
